import { CaretDownIcon, CaretUpIcon, ArrowUpRightFromSquareIcon } from "@octopusdeploy/design-system-icons";
import React from "react";
import type { DesignSystemLinkHref } from "../../routing";
import { useOctopusLinkComponent } from "../../routing";
import { getButtonStyles, buttonLabelStyles, getButtonIconStyles } from "./buttonStyles";
interface SharedButtonProps {
    /**
     * The importance of the button.
     */
    importance: ButtonImportance;
    /**
     * Controls whether the button is disabled.
     */
    disabled?: boolean;
    /**
     * The size of the button.
     */
    size?: ButtonSize;
    /**
     * The title to show when hovering the button.
     */
    title?: string;
    /**
     * The accessible name for the button.
     */
    accessibleName?: string;
    /**
     * The button icon. Will display to the left of the label if it exists.
     */
    icon?: React.ReactNode;
}
export interface ButtonLinkProps extends SharedButtonProps {
    /**
     * The link href.
     */
    href: DesignSystemLinkHref;
    /**
     * Set this to true for links that navigate outside of the application.
     */
    external?: boolean;
    /**
     * Controls whether the link should open in a new tab.
     * For external links, this is always true and cannot be configured.
     */
    opensInNewTab?: boolean;
    /**
     * The link label.
     */
    label: string;
    /**
     * Optional callback for when the link is clicked.
     */
    onClick?: ClickHandler;
}
export interface ButtonBasicProps extends SharedButtonProps {
    /**
     * Callback for when the button is clicked.
     */
    onClick: ClickHandler;
    /**
     * The button label.
     */
    label?: string;
    /**
     * Display a custom menu caret icon next to the label.
     */
    showExpandCollapseIcon?: boolean;
    /**
     * Identifies the element (or elements) whose contents or presence are controlled by the button.
     */
    "aria-controls"?: string;
    /**
     * Indicates the availability and type of interactive popup element that can be triggered by an element.
     * Only "menu" is currently supported.
     */
    "aria-haspopup"?: "menu";
    /**
     * Indicates whether the  element this button controls is currently expanded or collapsed.
     */
    "aria-expanded"?: "true" | "false";
}
type ClickHandler = (event: React.MouseEvent) => void;
export type ButtonProps = ButtonBasicProps | ButtonLinkProps;
export function Button(props: ButtonProps) {
    const title = props.title ?? props.label;
    if (isLinkButtonProps(props)) {
        return (<LinkButton label={props.label} href={props.href} onClick={props.onClick} importance={props.importance} size={props.size} disabled={props.disabled} icon={props.icon} external={props.external} opensInNewTab={props.opensInNewTab} title={title} accessibleName={props.accessibleName}/>);
    }
    return (<BasicButton onClick={props.onClick} label={props.label} importance={props.importance} size={props.size} disabled={props.disabled} icon={props.icon} showExpandCollapseIcon={props.showExpandCollapseIcon} title={title} accessibleName={props.accessibleName} aria-controls={props["aria-controls"]} aria-haspopup={props["aria-haspopup"]} aria-expanded={props["aria-expanded"]}/>);
}
function isLinkButtonProps(props: ButtonProps): props is ButtonLinkProps {
    return "href" in props && !!props.href;
}
function LinkButton({ external, disabled = false, size = "medium", onClick, href, importance, label, title, accessibleName, icon, ...props }: ButtonLinkProps) {
    const Link = useOctopusLinkComponent();
    const buttonStyles = getButtonStyles(importance, disabled, size, !!label);
    const buttonIconStyles = getButtonIconStyles(size);
    const opensInNewTab = external ? true : props.opensInNewTab;
    const handleOnClick: React.MouseEventHandler = React.useCallback((event) => {
        onClick?.(event);
    }, [onClick]);
    const linkContent = (<>
            {icon && <span className={buttonIconStyles}>{icon}</span>}
            {label && <span className={buttonLabelStyles}>{label}</span>}
            {opensInNewTab && (<span className={buttonIconStyles}>
                    <ArrowUpRightFromSquareIcon size={20}/>
                </span>)}
        </>);
    if (disabled) {
        return (<a role="link" className={buttonStyles} aria-disabled={true} title={title}>
                {linkContent}
            </a>);
    }
    const target = opensInNewTab ? "_blank" : "_self";
    if (external) {
        const stringHref = typeof href === "string" ? href : undefined;
        return (<a className={buttonStyles} target={target} rel="noreferrer" href={stringHref} onClick={handleOnClick} title={title}>
                {linkContent}
            </a>);
    }
    return (<Link className={buttonStyles} target={target} href={href} onClick={handleOnClick} aria-label={accessibleName} title={title}>
            {linkContent}
        </Link>);
}
function BasicButton({ icon, importance, size = "medium", onClick, disabled = false, label, title, accessibleName, "aria-controls": ariaControls, "aria-haspopup": ariaHasPopup, "aria-expanded": ariaExpanded, showExpandCollapseIcon, }: ButtonBasicProps) {
    const buttonStyles = getButtonStyles(importance, disabled, size, !!label);
    const buttonIconStyles = getButtonIconStyles(size);
    // FFT TODO Temporary - need to update every instance to set menuIcon explicitly
    const showMenuIcon = ariaExpanded ? showExpandCollapseIcon ?? true : false;
    const caretIcon = ariaExpanded === "true" ? <CaretUpIcon size={20}/> : <CaretDownIcon size={20}/>;
    return (<button className={buttonStyles} onClick={onClick} disabled={disabled} title={title ?? label} aria-label={accessibleName} aria-controls={ariaControls} aria-haspopup={ariaHasPopup} aria-expanded={ariaExpanded}>
            {icon && <span className={buttonIconStyles}>{icon}</span>}
            {label && <span className={buttonLabelStyles}>{label}</span>}
            {label && showMenuIcon && <span className={buttonIconStyles}>{caretIcon}</span>}
        </button>);
}
export const buttonImportanceLevels = ["primary", "secondary", "tertiary", "destructive", "quiet", "loud", "neutral", "ghost"] as const;
export type ButtonImportance = (typeof buttonImportanceLevels)[number];
export const buttonSizes = ["medium", "small"] as const;
export type ButtonSize = (typeof buttonSizes)[number];
